// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

/* istanbul ignore file */
import { type AnyAction, type Store } from '@reduxjs/toolkit';
import { type OptimizationPayload } from '@shared/data-access/interfaces/api/ai/portfolio';
import { type AppState as RootState } from '@advisor-app/store/store';
import { type StockFilters } from '@shared/data-access/interfaces/api/ai/screener';
import { type Universe } from '@shared/data-access/interfaces/redux/parameters-state';
import {
  type AssetsTypeState,
  AssetTypes,
} from '@shared/data-access/interfaces/redux/assets-type-state-types';

export const esg_scoring_limits = [
  {
    factor: 'co2_emissions',
    ranges: [0, 100],
  },
  {
    factor: 'environment_score',
    ranges: [0, 100],
  },
  {
    factor: 'governance_score',
    ranges: [0, 100],
  },
  {
    factor: 'social_score',
    ranges: [0, 100],
  },
];

export const generatePayloadCustomUniverse = (universe: Universe): StockFilters => {
  const {
    factors_filter,
    sectors_filter,
    esg_exclusions,
    esg_scoring,
    esg_not_informed,
    to_exclude,
    to_include,
    universe_base,
  } = universe;

  const factors_keys: string[] = [];
  const lower: number[] = [];
  const upper: number[] = [];

  factors_filter.forEach(factor => {
    factors_keys.push(factor.factor);
    lower.push(factor.ranges[0] / 100);
    upper.push(factor.ranges[1] / 100);
  });

  const esg_factors_keys: string[] = [];
  const esg_lower: number[] = [];
  const esg_upper: number[] = [];

  esg_scoring?.forEach((factor, idx) => {
    if (esg_scoring_limits[idx].ranges.toString() !== factor.ranges.toString()) {
      esg_factors_keys.push(factor.factor);
      if (factor.factor === 'co2_emissions') {
        esg_lower.push(factor.ranges[0] * 5000000);
        esg_upper.push(factor.ranges[1] * 5000000);
      } else {
        esg_lower.push(factor.ranges[0]);
        esg_upper.push(factor.ranges[1]);
      }
    }
  });

  return {
    universe_base: {
      name: universe_base,
      description: universe_base,
    },
    factors_filter: {
      factor: factors_keys,
      lower,
      upper,
    },
    sectors_filter,
    esg_scoring: {
      filter: esg_factors_keys,
      lower: esg_lower,
      upper: esg_upper,
    },
    esg_exclusions,
    esg_not_informed,
    to_include,
    to_exclude,
  };
};

export const generatePayloadOptimization = (
  store: Store<RootState, AnyAction>,
  type?: string,
) => {
  const typeAsset = store.getState().workflow.asset;
  const holdings = store.getState().portfolioPositions.current;
  const universe = store.getState().parameters.universe.entities['ai-builder']!;
  const tickers = Object.values(
    store.getState().assetsType.entities,
  ) as AssetsTypeState[];
  const optimizationConfig =
    store.getState().parameters.optimizationConfig.entities['ai-builder']!;
  const trades = store.getState().portfolioPositions.trades;

  const payload: Record<string, any> = {
    investment_portfolio: { holdings },
    trades,
    opt_configuration: {
      ...optimizationConfig,
      benchmark: optimizationConfig.benchmark[0].ticker,
    } as OptimizationPayload['opt_configuration'],
  };

  if (typeAsset === 'stock' || type === 'stock') {
    payload.universe = generatePayloadCustomUniverse(universe);
  }

  if (typeAsset === 'etf') {
    payload.etfs = tickers
      .filter(ticker => ticker?.type === AssetTypes.ETFS)
      .map(({ ticker }) => ticker);
  }

  if (typeAsset === 'mutual_fund') {
    payload.mutual_funds = tickers
      .filter(ticker => ticker?.type === AssetTypes.MUTUAL_FUNDS)
      .map(({ ticker }) => ticker);
  }

  return { payloadOptimization: payload as OptimizationPayload, trades };
};
